// Which filters each BKG search view shows up front. The rest of the schema
// sits behind "More filters". Paths a workspace's schema lacks are skipped, so
// a list can name fields that only some workspaces have.

import type { FilterInterface } from "./SearchFilters";
import { leafKind } from "./filterState";

// Leaf paths, in the order the rail shows them. The name filter leads each list.
export const COMPANY_PRIMARY_FACETS: readonly string[] = [
  "value.name.value",
  "value.lead_pipeline_asset.value.therapeutic_area.value",
  "value.lead_pipeline_asset.value.development_stage.value",
  "value.lead_pipeline_asset.value.technology.value",
  "value.headquarters.value.country.value",
  "value.number_of_employees.value",
  "value.latest_funding.value.amount.value",
  "value.company_entity_type.value",
  "value.attendance.value.event_name.value",
  // Only workspaces with Personal Data Vault enabled have this path; the API
  // drops it from every other workspace's filter schema.
  "value.ssi_connections.value.strength.value",
];

export const VC_PRIMARY_FACETS: readonly string[] = [
  "value.name.value",
  "value.stage_focus.value",
  "value.focus_sectors.value.sector.value",
  "value.check_size_usd.value",
  "value.aum_usd.value",
  "value.investment_style.value",
  "value.headquarters.value.country.value",
  "value.active_funds.value.vintage_year.value",
];

// The catalyst event filter nests the whole company filter under this path.
const EVENT_COMPANY = "value.company_name.value";

export const CATALYST_EVENT_PRIMARY_FACETS: readonly string[] = [
  `${EVENT_COMPANY}.name.value`,
  "value.type.value",
  "value.date.value",
  "source_date",
  `${EVENT_COMPANY}.lead_pipeline_asset.value.therapeutic_area.value`,
  `${EVENT_COMPANY}.lead_pipeline_asset.value.development_stage.value`,
  `${EVENT_COMPANY}.headquarters.value.country.value`,
];

export type FacetSplit = {
  /** The listed leaves the schema has and can render, in list order. */
  primary: FilterInterface[];
  /** The schema without them, or null when nothing is left. */
  more: FilterInterface | null;
};

const collectLeaves = (
  node: FilterInterface,
  out: Map<string, FilterInterface>,
) => {
  if (!node.children) {
    if (leafKind(node) !== null) out.set(node.path, node);
    return;
  }
  for (const child of node.children) collectLeaves(child, out);
};

// Groups left with no children are dropped too.
const withoutLeaves = (
  node: FilterInterface,
  paths: Set<string>,
): FilterInterface | null => {
  if (!node.children) return paths.has(node.path) ? null : node;
  const children = node.children
    .map((child) => withoutLeaves(child, paths))
    .filter((child): child is FilterInterface => child !== null);
  return children.length ? { ...node, children } : null;
};

export const splitFacets = (
  schema: FilterInterface,
  primaryPaths: readonly string[],
): FacetSplit => {
  const leaves = new Map<string, FilterInterface>();
  collectLeaves(schema, leaves);
  const primary = Array.from(new Set(primaryPaths)).flatMap(
    (path) => leaves.get(path) ?? [],
  );
  if (primary.length === 0) return { primary, more: schema };
  const lifted = new Set(primary.map((leaf) => leaf.path));
  return { primary, more: withoutLeaves(schema, lifted) };
};
